import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import Flx from "../../../../components/layout/Flx";
import { useFundingStatementsHook } from "../../../../hooks/useFundingStatementsHook";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import { loanFetchedDataLoanTermsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import FundingEntityGate from "./FundingEntityGate";
import FundingWorkRail, { PANES, PANE_SLUGS } from "./FundingWorkRail";
import WireReconciliationBar from "./WireReconciliationBar";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import FundingShieldPane from "./panes/FundingShieldPane";
import StatementPane from "./panes/StatementPane";
import TieOutPane from "./panes/TieOutPane";
import WireTicketPane from "./panes/WireTicketPane";

/**
 * Wire & Funding — the closer's pre-funding workspace.
 *
 * LAYOUT: a reconciliation bar over a rail + one full-width pane.
 *
 * The tab used to be three stacked cards, and stacking made the funding
 * lifecycle a scroll. But three side-by-side columns is the wrong correction:
 * each artifact here is a DOCUMENT with real width requirements — a TFS prints
 * Title against Correspondent, the NWC prints sources against uses, the Funding
 * Shield renders a live PDF preview. Squeeze any of them into a third of the
 * viewport and it becomes scroll again.
 *
 * What actually has to be visible at once is not three documents, it is the
 * handful of NUMBERS that must agree — so those are lifted into
 * `WireReconciliationBar` at the top, permanently:
 *
 *   Gate A — three independently computed net-wire figures
 *            (statement.netWireToTitle, netWire.netWireToTitle,
 *             wireInstructions.wireAmount) must be the same number.
 *   Gate B — the backend's own HUD tie-out (hudSourcesTotal − hudUsesTotal,
 *            which must be exactly zero).
 *
 * With the comparison handled, the artifacts don't need to share a row. The
 * left rail keeps all four states visible and switches the pane; the pane gets
 * the whole width.
 *
 * DATA: this shell owns every fetch on the tab — loan terms (for the wire ticket
 * + Funding Shield forms), the funding statement, and the net wire calculator —
 * and passes payloads down. The panes and the bar are pure presenters, which is
 * what lets the reconciliation bar, the rail and the release checks in the
 * ticket pane all agree instead of each fetching its own copy.
 */
const LoanDrilldownWireAndFundingTab = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const dealId = loanDrilldown?._id;

  const { getLoanTerms } = useLoanTermsHook();
  const { isCloser } = usePermissionsHook();
  const { downloadWireWorksheet } = useLoanFilesHook();
  const {
    getFundingStatement,
    getNetWire,
    viewFundingStatementPdf,
    viewNetWirePdf,
    lockFundingStatement,
    acknowledgeFundingStatement,
  } = useFundingStatementsHook();

  // The selected pane IS the URL. Deep links, the browser back button and the
  // "Open Funding Shield" link on Loan Terms all land on the right pane because
  // none of them go through component state.
  const navigate = useNavigate();
  // Pipeline-aware base ("/loan/:id" preclose, "/acquisition/:id" postclose).
  // Hardcoding "/loan/" here kicks an acquisitions loan out of its own drilldown
  // and into the preclose one, whose getLoanById hits /underwriting/* -> 404.
  const baseUrl = useDrilldownBaseUrl(dealId);
  const { "*": splat } = useParams();
  const paneSlug = String(splat || "").split("/")[0];
  const pane = PANE_SLUGS.includes(paneSlug) ? paneSlug : null;
  const setPane = useCallback(
    (next) => navigate(`${baseUrl}/wire-and-funding/${next}`),
    [navigate, baseUrl],
  );

  const [statement, setStatement] = useState(null);
  const [statementFetching, setStatementFetching] = useState(true);
  const [statementFailed, setStatementFailed] = useState(false);
  const [locking, setLocking] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  // Set by "Skip for now" on the gate. Session-only and per-loan on purpose —
  // it is not persisted and it resets below when the loan changes, so skipping
  // never silently follows a closer onto the next file.
  const [gateSkipped, setGateSkipped] = useState(false);

  const [netWire, setNetWire] = useState(null);
  const [netWireFetching, setNetWireFetching] = useState(true);
  const [netWireFailed, setNetWireFailed] = useState(false);
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);
  // An unparsed HUD was picked and queued for extraction: "Reading the
  // statement…" and one refetch about a minute later (funding-statements.md).
  const [readingStatement, setReadingStatement] = useState(false);
  const readingTimer = useRef(null);
  useEffect(() => () => clearTimeout(readingTimer.current), []);

  useEffect(() => {
    setGateSkipped(false);
  }, [dealId]);

  const wireInstructions = loanTerms?.wireInstructions;

  // Loan terms feed the wire-ticket form and the Funding Shield PDF; both read
  // state.loanFetchedData.loanTerms and neither fetches on its own.
  useEffect(() => {
    if (isNil(loanTerms) && dealId) {
      getLoanTerms({
        dealId,
        onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        onFailFn: () => dispatch(loanFetchedDataLoanTermsSet({})),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanTerms, dealId]);

  const fetchStatement = useCallback(() => {
    if (!dealId) return;
    setStatementFetching(true);
    setStatementFailed(false);
    getFundingStatement({
      dealId,
      onSuccessFn: (data) => setStatement(data || null),
      onFailFn: () => setStatementFailed(true),
      onCompleteFn: () => setStatementFetching(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const fetchNetWire = useCallback(() => {
    if (!dealId) return;
    setNetWireFetching(true);
    setNetWireFailed(false);
    getNetWire({
      dealId,
      onSuccessFn: (data) => setNetWire(data || null),
      onFailFn: () => setNetWireFailed(true),
      onCompleteFn: () => setNetWireFetching(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  useEffect(() => {
    fetchStatement();
    fetchNetWire();
  }, [fetchStatement, fetchNetWire]);

  // POST hud_source answers with the rebuilt calculator — render from it
  // rather than refetching.
  const handleHudSourceChanged = useCallback(
    (res) => {
      if (res?.netWireCalculator) setNetWire(res.netWireCalculator);
      clearTimeout(readingTimer.current);
      if (res?.extractionQueued) {
        setReadingStatement(true);
        readingTimer.current = setTimeout(() => {
          setReadingStatement(false);
          fetchNetWire();
        }, 60000);
      } else {
        setReadingStatement(false);
      }
    },
    [fetchNetWire],
  );

  const statementLocked = ["Locked", "Acknowledged"].includes(
    statement?.statementStatus,
  );

  const handleLock = useCallback(() => {
    setLocking(true);
    lockFundingStatement({
      dealId,
      onSuccessFn: () => {
        // The lock also snapshots the NWC, so both are refetched.
        fetchStatement();
        fetchNetWire();
      },
      onCompleteFn: () => setLocking(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, fetchStatement, fetchNetWire]);

  const handleAcknowledge = useCallback(
    (name, onDone) => {
      setAcknowledging(true);
      acknowledgeFundingStatement({
        dealId,
        name,
        method: "signed_pdf",
        onSuccessFn: () => {
          if (onDone) onDone();
          fetchStatement();
        },
        onCompleteFn: () => setAcknowledging(false),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, fetchStatement],
  );

  const handleDownloadWorksheet = useCallback(() => {
    setDownloadingWorksheet(true);
    downloadWireWorksheet({
      dealId,
      syncLoanId: netWire?.syncLoanId || loanDrilldown?.syncLoanId,
      onSuccessFn: () => setDownloadingWorksheet(false),
      onFailFn: () => setDownloadingWorksheet(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, netWire?.syncLoanId, loanDrilldown?.syncLoanId]);

  // Bare (or unrecognised) sub-path canonicalises to the first pane, so the URL
  // always names what is on screen.
  if (dealId && baseUrl && !pane) {
    return (
      <Navigate
        to={`${baseUrl}/wire-and-funding/${PANES.STATEMENT}`}
        replace
      />
    );
  }

  if (isNil(loanTerms)) {
    return (
      <LoanDrilldownTabPanel>
        <Flx center sx={{ p: 7 }}>
          <CircularProgress />
        </Flx>
      </LoanDrilldownTabPanel>
    );
  }

  // GATE — the funding entity is the entity that actually sends the wire. It is
  // the named account holder on the NWC (`fundingEntityName`), the lender block
  // printed on the Funding Shield sheet (`fundingEntity.mortgageeClause`), and
  // the mortgagee on the note. Without it every artifact on this tab is either
  // blank in the place that identifies the funder or, worse, quietly plausible —
  // so the tab refuses to be worked rather than producing a sheet with a hole in
  // it. See FundingEntityGate.
  // "Skip for now" on the gate opts out of this for the current loan only, for
  // the closer who just wants to look at what is there.
  if (
    (isNil(loanTerms?.fundingEntity) || isEmpty(loanTerms?.fundingEntity)) &&
    !gateSkipped
  ) {
    return (
      <LoanDrilldownTabPanel sx={{ overflow: "visible" }}>
        <Box sx={{ py: 4, px: 3 }}>
          <Box sx={{ maxWidth: "1600px", mx: "auto" }}>
            <FundingEntityGate
              dealId={dealId}
              onSkip={() => setGateSkipped(true)}
            />
          </Box>
        </Box>
      </LoanDrilldownTabPanel>
    );
  }

  return (
    <LoanDrilldownTabPanel sx={{ overflow: "visible" }}>
      {/* overflow VISIBLE, deliberately. LoanDrilldownTabPanel defaults to
          overflow:hidden, which makes it the nearest scrollport for anything
          position:sticky inside the tab — and since the page (not this box)
          is what actually scrolls, that scrollport never moves and sticky is
          silently dead. Letting it be visible hands scrolling back to the page,
          which is what the Funding Shield preview column sticks against. */}
      {/* Same shell as the DSCR Calculations tab: page padding outside, a
          1600px cap centered inside, so the drilldown tabs line up on wide
          monitors instead of each stretching to its own width. */}
      <Box sx={{ py: 4, px: 3 }}>
        <Box sx={{ maxWidth: "1600px", mx: "auto", minWidth: 0 }}>
          <WireReconciliationBar
            statement={statement}
            netWire={netWire}
            wireInstructions={wireInstructions}
          />

          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "272px minmax(0, 1fr)",
              },
            }}
          >
            {/* The wrapper stretches to the row height so the rail inside it
                has somewhere to travel while the pane scrolls. */}
            <Box sx={{ minWidth: 0 }}>
              <FundingWorkRail
                active={pane}
                onSelect={setPane}
                statement={statement}
                netWire={netWire}
                wireInstructions={wireInstructions}
                loan={loanDrilldown}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              {pane === PANES.STATEMENT ? (
                <StatementPane
                  statement={statement}
                  loading={statementFetching}
                  failed={statementFailed}
                  isCloser={isCloser}
                  onViewPdf={() => viewFundingStatementPdf({ dealId })}
                  onLock={handleLock}
                  locking={locking}
                  onAcknowledge={handleAcknowledge}
                  acknowledging={acknowledging}
                />
              ) : null}

              {pane === PANES.TIE_OUT ? (
                <TieOutPane
                  netWire={netWire}
                  loading={netWireFetching}
                  failed={netWireFailed}
                  onViewPdf={() => viewNetWirePdf({ dealId })}
                  onDownloadWorksheet={handleDownloadWorksheet}
                  downloadingWorksheet={downloadingWorksheet}
                  dealId={dealId}
                  statementLocked={statementLocked}
                  onHudSourceChanged={handleHudSourceChanged}
                  readingStatement={readingStatement}
                />
              ) : null}

              {pane === PANES.TICKET ? (
                <WireTicketPane
                  statement={statement}
                  netWire={netWire}
                  wireInstructions={wireInstructions}
                />
              ) : null}

              {pane === PANES.SHIELD ? (
                <FundingShieldPane />
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
    </LoanDrilldownTabPanel>
  );
};

export default LoanDrilldownWireAndFundingTab;
